import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";
import IHelm from "./pages/IHelm";
import CetaFuel from "./pages/CetaFuel";
import About from "./pages/About";
import AboutUs from "./pages/AboutUs";
import Contact from "./pages/Contact";
import Careers from "./pages/Careers";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Solutions from "./pages/Solutions";
import Segment from "./pages/Segment";
import NotFound from "./pages/NotFound";
import Innovation from "./pages/Innovation";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import BlogAdmin from "./pages/BlogAdmin";
import BlogEditor from "./pages/BlogEditor";
import InsightCategory from "./pages/InsightCategory";
import WhitePapers from "./pages/WhitePapers";
import Projects from "./pages/Projects";
// WhitePaperPost is no longer needed — BlogPost handles all /:slug posts
import CookieConsent from "./components/CookieConsent";
import { wordPressRedirectRoutes } from "./components/WordPressRedirects";
import BookDemo from "./pages/BookDemo";
import WhitePaperDownload from "./pages/WhitePaperDownload";
import WhitePaperIHelmDownload from "./pages/WhitePaperIHelmDownload";
import WhitePaperIHelmIntro from "./pages/WhitePaperIHelmIntro";
import WhitePaperEnergyIntro from "./pages/WhitePaperEnergyIntro";
import WhitePaperEnergyDownload from "./pages/WhitePaperEnergyDownload";
import PartnerForm from "./pages/PartnerForm";
import ElectriseaInformation from "./pages/ElectriseaInformation";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    },
  },
});

// Redirect component for old /segment/:slug → /segments/:slug
const SegmentRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/segments/${slug}`} replace />;
};

// Redirect component for old /whitepapers/:slug
const WhitepaperRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/${slug}`} replace />;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/ihelm-intelligent-platform" element={<IHelm />} />
              <Route path="/cetafuel" element={<CetaFuel />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/about" element={<About />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/career-at-cetasol-team" element={<Careers />} />
              <Route path="/privacy-policy" element={<Privacy />} />
              <Route path="/term_conditions" element={<Terms />} />
              <Route path="/segments/:slug" element={<Segment />} />
              {/* WordPress legacy redirects */}
              <Route path="/segment/:slug" element={<SegmentRedirect />} />
              <Route path="/book-a-demo" element={<BookDemo />} />
              <Route path="/book-ihelm-demo" element={<Navigate to="/book-a-demo" replace />} />
              <Route path="/cookie-policy-eu" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/whitepapers/:slug" element={<WhitepaperRedirect />} />
              {wordPressRedirectRoutes}
              <Route path="/ihelm-intelligent-platform/" element={<IHelm />} />
              <Route path="/cetafuel/" element={<CetaFuel />} />
              <Route path="/privacy-policy/" element={<Privacy />} />
              <Route path="/technology" element={<Innovation />} />
              <Route path="/news" element={<Blog />} />
              <Route path="/insight/:category" element={<InsightCategory />} />
              <Route path="/admin/blog" element={<BlogAdmin />} />
              <Route path="/admin/blog/new" element={<BlogEditor />} />
              <Route path="/admin/blog/edit/:id" element={<BlogEditor />} />
              <Route path="/whitepaper/cetafuel-download" element={<WhitePaperDownload />} />
              <Route path="/whitepaper/ihelm-download" element={<WhitePaperIHelmDownload />} />
              <Route path="/partner-form" element={<PartnerForm />} />
              <Route path="/Electrisea-information" element={<ElectriseaInformation />} />
              <Route path="/electrisea-information" element={<ElectriseaInformation />} />
              <Route path="/whitepapers" element={<WhitePapers />} />
              <Route path="/ihelm-white-paper" element={<WhitePaperIHelmIntro />} />
              <Route path="/energy-optimization-white-paper" element={<WhitePaperEnergyIntro />} />
              <Route path="/whitepaper/energy-optimization-download" element={<WhitePaperEnergyDownload />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/:slug" element={<BlogPost />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsent />
          </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
